// Excel / PDF export of the VAT transactions table exactly as the viewer shows it (sort order, search filter, row numbers).
import * as XLSX from 'xlsx-js-style';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT, REPORT_STYLES } from './ExcelReport';
import { parseBeautifiedNumber } from './FormatNumber';
import EnumsService from '../Services/enums.service';

const safeFileName = (value) => String(value || 'Report').replace(/[\\/:*?"<>|]/g, '_');

const cellText = (cell) => (cell?.textContent || '').replace(/\u00a0/g, ' ').trim();

const toNumber = (text) => {
    if (!text) return '';
    const n = parseBeautifiedNumber(text);
    return Number.isFinite(n) ? n : text;
};

const MISSING_ROW_FILL = { fgColor: { rgb: 'FFD0D0' } };

const rowKey = (id, type) => `${type}:${id}`;

/**
 * Builds a table clone ready for Excel/PDF: optional selected-rows-only filter, strips the checkbox
 * column, drops hidden rows, and renumbers. After stripping select, money columns sit at [7,8,9,10].
 */
export const prepareVatTransactionsExportTable = (table, { selectedKeys = null } = {}) => {
    if (!table) return { table: null, amountColumns: [7, 8, 9, 10] };

    const clone = table.cloneNode(true);
    clone.querySelectorAll('.sort-ind').forEach((el) => el.remove());

    if (selectedKeys && selectedKeys.size > 0) {
        clone.querySelectorAll('tbody tr.tr[data-id]').forEach((tr) => {
            const key = rowKey(tr.getAttribute('data-id'), tr.getAttribute('data-type'));
            if (!selectedKeys.has(key)) tr.remove();
        });
        // Selection export is a flat list — month headers / report totals no longer apply.
        clone.querySelectorAll('tbody tr.month-header, tbody tr.tr-final-row').forEach((tr) => tr.remove());
    } else {
        clone.querySelectorAll('tbody > tr[hidden]').forEach((tr) => tr.remove());
    }

    clone.querySelectorAll('th.row-select, td.row-select').forEach((el) => el.remove());

    let n = 0;
    clone.querySelectorAll('tbody tr.tr[data-id]').forEach((tr) => {
        n += 1;
        const num = tr.querySelector('td.row-num');
        if (num) num.textContent = String(n);
    });

    return { table: clone, amountColumns: [7, 8, 9, 10], rowCount: n };
};

/**
 * `table` is the rendered table (row-number column included); `amountColumns` are the indexes, in that table,
 * of the money columns. Month headings become merged rows, monthly/grand totals keep the total style.
 */
export const downloadVatTransactionsExcel = ({ table, title, caseId, period, amountColumns = [] }) => {
    const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
        cellText(th).replace(/[▲▼]/g, '').trim()
    );
    const colCount = headers.length;
    const amounts = new Set(amountColumns);

    const titleLines = [title, [caseId, period].filter(Boolean).join('    ')].filter(Boolean);
    const aoa = titleLines.map((line) => [line]);
    aoa.push([]);
    const headerRow = aoa.length;
    aoa.push(headers);

    const kinds = [];
    Array.from(table.querySelectorAll('tbody > tr')).forEach((tr) => {
        if (tr.hidden) return;
        if (tr.classList.contains('month-header')) {
            aoa.push([cellText(tr)]);
            kinds.push('month');
            return;
        }
        const cells = Array.from(tr.children).map((td, c) => {
            const text = cellText(td);
            if (amounts.has(c)) return toNumber(text);
            if (c === 0 && /^\d+$/.test(text)) return Number(text);
            return text;
        });
        aoa.push(cells);
        if (tr.classList.contains('tr-final-row')) kinds.push('total');
        else kinds.push(tr.classList.contains('missing-row') ? 'missing' : 'data');
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const lastCol = Math.max(colCount - 1, 0);
    ws['!merges'] = titleLines.map((_, r) => ({ s: { r, c: 0 }, e: { r, c: lastCol } }));
    ws['!cols'] = headers.map((_, c) => ({ wch: c === 0 ? 6 : amounts.has(c) ? 14 : 18 }));
    ws['!views'] = [{ RTL: true }];

    titleLines.forEach((_, r) => {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) ws[addr].s = r === 0 ? REPORT_STYLES.reportTitle : REPORT_STYLES.subTitle;
    });
    for (let c = 0; c < colCount; c += 1) {
        const addr = XLSX.utils.encode_cell({ r: headerRow, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = REPORT_STYLES.header;
    }

    let dataIndex = 0;
    kinds.forEach((kind, i) => {
        const r = headerRow + 1 + i;
        if (kind === 'month') {
            ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: lastCol } });
        }
        const stripe = kind === 'data' && dataIndex % 2 === 1;
        if (kind === 'data' || kind === 'missing') dataIndex += 1;
        for (let c = 0; c < colCount; c += 1) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (!ws[addr]) ws[addr] = { t: 's', v: '' };
            const cell = ws[addr];
            const isAmount = amounts.has(c);
            if (kind === 'month' || kind === 'total') {
                cell.s = isAmount ? REPORT_STYLES.totalNum : REPORT_STYLES.totalText;
            } else {
                const base = stripe ? REPORT_STYLES.stripeText : REPORT_STYLES.dataText;
                cell.s = kind === 'missing' ? { ...base, fill: MISSING_ROW_FILL } : base;
            }
            if (isAmount && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
        }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, safeFileName(title).slice(0, 31));
    applyWorkbookRtl(wb);
    XLSX.writeFile(wb, `${safeFileName(title)}-${safeFileName(caseId)}.xlsx`);
};

/**
 * Prints the report document the server sent, with its table swapped for the rendered one, so the PDF
 * carries the same order and row numbers as the screen.
 */
export const downloadVatTransactionsPdf = async ({
    html,
    table,
    title,
    caseId,
    token,
    selectedKeys = null,
}) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const original = doc.querySelector('table.table');
    if (!original) throw new Error('Report table not found');

    const { table: printed } = prepareVatTransactionsExportTable(table, { selectedKeys });
    if (!printed) throw new Error('Report table not found');
    original.replaceWith(doc.importNode(printed, true));

    const style = doc.createElement('style');
    style.textContent = `
        @page { size: A4 landscape; margin: 8mm 6mm; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        th.row-num, td.row-num { width: 34px; text-align: center; color: #5b6478; }
        th.row-select, td.row-select { display: none; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
    `;
    doc.head.appendChild(style);

    const res = await EnumsService.GeneratePdfFromHtml(`<!DOCTYPE html>${doc.documentElement.outerHTML}`, caseId, token);
    if (!res?.ok) throw new Error('PDF generation failed');

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const data = await res.json();
        const pdfBase64 = data.data?.PdfBase64 || data.data?.pdfBase64 || data.PdfBase64;
        if (!pdfBase64) throw new Error('No PDF base64 in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeFileName(title)}-${safeFileName(caseId)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
